import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { AccountData, OrderPageData } from '../../api';
import { OrderPageStatus } from '../../api/models/OrderPageStatus';
import { getOrderPages } from '../../utils/AccountUtils';
import { useAuth } from '../../session/InternalAuthProvider';
import { useLanguage } from '../../common/GeneralUtils';
import { ALL_ORDER_STATUSES, useOrdersFilter } from './OrdersFilterContext';

type SummaryLabel = { primary: string; secondary: string };

export const useOrdersFilterSummary = (hideStatuses?: boolean): SummaryLabel => {
  const { t } = useTranslation();
  const language = useLanguage();
  const { authState } = useAuth();
  const account = authState.account as unknown as AccountData;
  const { isFilterActive, statuses, selectedOrderPageIds } = useOrdersFilter();

  return useMemo(() => {
    if (!isFilterActive) return { primary: t('orders.filter.label'), secondary: '' };

    const parts: string[] = [];

    if (selectedOrderPageIds !== null) {
      const activeOrderPages = getOrderPages(account).filter(
        (op: OrderPageData) => op.status !== OrderPageStatus.ARCHIVED,
      );
      const total = activeOrderPages.length;
      const selectedCount = selectedOrderPageIds.length;
      parts.push(t('orders.filter.summary.workshops', { count: selectedCount, total }));
    }

    const secondaryParts: string[] = [];
    if (!hideStatuses && statuses.length > 0 && statuses.length < ALL_ORDER_STATUSES.length) {
      secondaryParts.push(
        t('orders.filter.summary.statuses', { count: statuses.length, total: ALL_ORDER_STATUSES.length }),
      );
    }

    return {
      primary: parts.join(' · ') || t('orders.filter.label'),
      secondary: secondaryParts.join(' · '),
    };
  }, [isFilterActive, statuses, selectedOrderPageIds, account, language, t, hideStatuses]);
};
